import type { IPbiComparisonEntry } from './types.js';

/**
 * Documented deviations between Skystone’s canonical definitions and the
 * Power BI dashboard `funnelin/PE_Dashboard_Visual_Definitions.docx`.
 *
 * Each entry is a deliberate, intentional deviation — Power BI parity is a
 * secondary outcome of PRODOPSAI-410. Where Skystone differs, the deviation is
 * documented here rather than papered over.
 */
export const PBI_COMPARISON: ReadonlyArray<IPbiComparisonEntry> = [
  {
    entryKey: 'lead-time-median-vs-mean',
    metricName: 'PR Lead Time',
    pbiBehavior: 'Reports the **mean** PR lead time per period (labelled "Average Lead Time").',
    skystoneBehavior:
      'Reports the **median** PR lead time per period, and labels the metric "PR Lead Time (median)" so the statistic is explicit rather than implied by "Average".',
    deviationReason:
      'Both measure the same window (PR created → merged). Mean lead time is dominated by long-tail outliers (a single very long-lived PR moves the chart). Skystone reports the median because it is more robust to outliers and better reflects a typical PR; the "(median)" label avoids the common misread of "Average" as an arithmetic mean.',
  },
  {
    entryKey: 'deployment-frequency-normalisation',
    metricName: 'Deployment Frequency',
    pbiBehavior: 'Reports deployments **per month** (calendar-month aggregation).',
    skystoneBehavior: 'Reports deployments **per week** with daily/weekly bucket options.',
    deviationReason:
      'DORA categorisation thresholds are defined per-week (e.g. "weekly = high performer"). Per-month aggregation makes the chart unactionable at the team level. Skystone normalises to per-week to align with DORA labelling.',
  },
  {
    entryKey: 'coverage-trend-aggregation',
    metricName: 'Coverage Trend',
    pbiBehavior: 'Aggregates coverage as a simple average of daily coverage values across the window.',
    skystoneBehavior:
      'Aggregates coverage as a weighted average over `lines_to_cover` so projects with more lines dominate the trend, matching SonarCloud’s own portfolio rollup.',
    deviationReason:
      'Simple averaging treats a 100-LOC test project the same as a 100k-LOC service, which is not the operational signal we want from a portfolio view.',
  },
  {
    entryKey: 'epic-completion-date-semantic',
    metricName: 'Epic Completion',
    pbiBehavior: 'Uses the epic’s `done_date` (when the parent epic itself transitioned to Done).',
    skystoneBehavior:
      'Uses the **latest child issue done date** so an epic counts as "completed in week W" only when its last story / bug / task closed in W.',
    deviationReason:
      'Epics often stay open for housekeeping after the last child closes. Using the parent transition date lags real delivery; the child-based semantic reflects when the work actually finished.',
  },
  {
    entryKey: 'open-absent-in-pbi',
    metricName: 'Open Issues',
    pbiBehavior: 'No equivalent metric. PBI never modelled "outstanding work."',
    skystoneBehavior:
      '`Open` is a Skystone-internal concept covering items that are not Done and not Cancelled, regardless of whether they are currently in progress.',
    deviationReason:
      'Skystone needed a single auditable filter for "outstanding work" so individual cards do not invent their own. Adding the concept here makes the filter visible and verifiable.',
  },
  {
    entryKey: 'wip-trend-granularity',
    metricName: 'WIP Trend',
    pbiBehavior:
      'Plots one point per day with a drillable date hierarchy (Year → Quarter → Month → Day).',
    skystoneBehavior:
      'Collapses to ISO-week buckets — each point is the average of the daily counts within that week.',
    deviationReason:
      'Cleaner trends across the metric-view default 90-day window; daily data is still available from the `/portfolio/wip-trend` endpoint for any consumer that wants raw daily granularity.',
  },
] as const;
